import React, { useMemo } from 'react';
import { Order } from "@/src/types";
import { Package } from "lucide-react";
import {
    getOrderItemAggregationKey,
    getOrderItemBaseName,
    getOrderItemOptionLabel,
} from '../../../utils/orderItemDisplay';

const sanitizeImageUrl = (url?: string) => {
    if (!url) return '';
    if (url.startsWith('http://')) {
        return url.replace('http://', 'https://');
    }
    return url;
};

export const PackingWaitingListView = ({ orders }: { orders: Order[] }) => {
    const pendingOrders = orders.filter(o => o.status === 'PENDING');

    const aggregated = useMemo(() => {
        const map: Record<string, {
            name: string;
            optionName?: string;
            quantity: number;
            image: string;
        }> = {};
        pendingOrders.forEach(order => {
            order.items.forEach(item => {
                if (item.quantity > 0) {
                    const itemKey = getOrderItemAggregationKey(item);
                    if (!map[itemKey]) {
                        map[itemKey] = {
                            name: getOrderItemBaseName(item),
                            optionName: getOrderItemOptionLabel(item),
                            quantity: 0,
                            image: item.image,
                        };
                    }
                    map[itemKey].quantity += item.quantity;
                }
            });
        });
        return Object.values(map).sort((a, b) => {
            const labelA = a.optionName ? `${a.name} ${a.optionName}` : a.name;
            const labelB = b.optionName ? `${b.name} ${b.optionName}` : b.name;
            return labelA.localeCompare(labelB, 'ko-KR');
        });
    }, [pendingOrders]);

    if (aggregated.length === 0) {
        return (
            <div className="py-20 flex flex-col items-center justify-center text-gray-400 gap-2">
                <Package size={48} strokeWidth={1} />
                <p className="text-sm font-bold">포장 대기 중인 상품이 없습니다.</p>
                <p className="text-xs">모든 '주문완료' 상태의 상품이 처리되었습니다.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="text-center space-y-2 mb-10">
                <h2 className="text-2xl font-black">포장 대기 목록</h2>
                <p className="text-sm font-bold text-gray-400">현재 미포장(주문완료) 상태인 상품들의 합계입니다.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {aggregated.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl border hover:border-emerald-200 transition-colors">
                        <div className="w-12 h-12 rounded-xl border bg-white overflow-hidden shrink-0 shadow-sm">
                            {sanitizeImageUrl(item.image) ? (
                                <img src={sanitizeImageUrl(item.image)} className="w-full h-full object-cover" alt="" />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-300">
                                    <Package size={18} />
                                </div>
                            )}
                        </div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-black text-gray-900 truncate">{item.name}</h4>
                            {item.optionName && (
                                <p className="text-[10px] font-black text-emerald-600 truncate">
                                    옵션: {item.optionName}
                                </p>
                            )}
                            <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">남은 포장 수량</p>
                        </div>
                        <div className="text-right">
                            <span className="text-xl font-black text-emerald-600">{item.quantity}개</span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
